<template>
  <div class="task-scope-builder">
    <!-- Guided scope builder: a top-to-bottom funnel. Pick a CML, optionally drill
         into a TML point, optionally a reading, then Add. Each level below CML is
         optional, so the same builder produces CML-only, CML→TML, and
         CML→TML→Reading links. -->
    <v-autocomplete
      v-model="builderCmlId"
      :items="cmlOptions"
      item-text="label"
      item-value="id"
      label="CML (monitoring location)"
      :placeholder="hasTags ? 'Search CMLs…' : 'Add a Tag above first'"
      :no-data-text="hasTags ? 'No CMLs on the selected tag(s)' : 'Add a Tag above first'"
      :loading="loadingCmls"
      :disabled="!hasTags"
      outlined
      dense
      hide-details
      clearable
      class="mb-2"
      data-test="scope-cml"
      :menu-props="{ offsetY: true, zIndex: 2100 }"
    />

    <v-autocomplete
      v-model="builderTmlId"
      :items="tmlOptions"
      item-text="label"
      item-value="id"
      label="TML point (optional — narrow down)"
      :loading="loadingTmls"
      :disabled="!builderCmlId"
      :no-data-text="builderCmlId ? 'No TML points on this CML' : 'Pick a CML first'"
      outlined
      dense
      hide-details
      clearable
      class="mb-2"
      data-test="scope-tml"
      :menu-props="{ offsetY: true, zIndex: 2100 }"
    />

    <v-autocomplete
      v-model="builderReadingId"
      :items="readingOptions"
      item-text="label"
      item-value="id"
      label="Thickness reading (optional — narrow down)"
      :loading="loadingReadings"
      :disabled="!builderTmlId"
      :no-data-text="builderTmlId ? 'No readings on this TML point' : 'Pick a TML point first'"
      outlined
      dense
      hide-details
      clearable
      class="mb-2"
      data-test="scope-reading"
      :menu-props="{ offsetY: true, zIndex: 2100 }"
    />

    <div class="task-scope-builder__actions">
      <v-btn
        small
        color="primary"
        :disabled="!builderCmlId"
        data-test="scope-add"
        @click="addScope"
      >
        <v-icon left small>mdi-plus</v-icon>
        Add scope
      </v-btn>
    </div>

    <!-- System-status: existing links are being fetched (reopen of a saved task). -->
    <div v-if="loadingExisting" class="task-scope-builder__loading" data-test="scope-loading">
      <v-progress-circular indeterminate size="14" width="2" class="mr-2" />
      Loading linked scopes…
    </div>

    <!-- Staged + existing links, deduped by composite key. Breadcrumb labels keep
         narrowed links human-readable (never raw GUIDs); title shows the full text
         when the row is truncated. -->
    <div v-if="selectedScopes.length" class="task-scope-builder__list" data-test="scope-list">
      <div
        v-for="s in selectedScopes"
        :key="s.key"
        class="task-scope-builder__item"
        data-test="scope-item"
      >
        <v-icon small class="mr-2">mdi-target</v-icon>
        <span class="task-scope-builder__item-label" :title="s.label">{{ s.label }}</span>
        <v-btn
          icon
          x-small
          class="ml-auto"
          :aria-label="`Remove ${s.label}`"
          :title="`Remove ${s.label}`"
          data-test="scope-remove"
          @click="removeScope(s.key)"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Transient feedback (e.g. a duplicate add was ignored). aria-live so screen
         readers announce it. -->
    <div
      v-if="notice"
      class="task-scope-builder__notice"
      role="status"
      aria-live="polite"
      data-test="scope-notice"
    >
      {{ notice }}
    </div>

    <div class="task-scope-builder__hint" aria-live="polite" data-test="scope-hint">{{ hint }}</div>
  </div>
</template>

<script>
import { log as debugLog, error as debugError } from '@/utils/debugLogger';
import {
  listCmlOptions,
  listTmlOptions,
  listReadingOptions,
  listTaskCmlLinks,
  linkTaskScope,
  unlinkCmlTask,
} from '@/features/Tags/components/monitoring/cmlTaskLinks';

/**
 * TaskCmlLinksSection — Wave C-ι, task side (Option A: tag-scoped) with NARROWING.
 *
 * A guided scope builder embedded in the task add/edit form under the Tags
 * selector. The user drills the hierarchy Tag → CML → TML Point → Thickness
 * Reading and stages monitoring "scopes" at any depth. The CML options are scoped
 * to the task's selected Tags (`tagIds`) — each CML belongs to a Master Tag.
 *
 * A scope's IDENTITY is the composite (cmlRecordId, tmlPointId, thicknessReadingId).
 * A null narrowing level is a DISTINCT scope, NOT a wildcard: "CR-002" (CML-only)
 * and "CR-002 › P-3" (narrowed to a TML) coexist and remove independently. (B1
 * "narrowing displaces broader" is intentionally NOT enforced here — independent
 * links are simpler and demo-correct.)
 *
 * Works for BOTH new and existing tasks: the selection is staged locally, and the
 * parent form calls `commit(taskId)` AFTER the task is saved. The component emits
 * `scopes-dirty` so the parent can enable its Save button when ONLY scopes changed
 * (the parent's own change-detection tracks task fields, not this child). `commit()`
 * swallows its own errors, so a link failure can never break the task save itself.
 */
export default {
  name: 'TaskCmlLinksSection',

  props: {
    taskId: { type: String, default: '' },
    isNew: { type: Boolean, default: false },
    // The task's linked Master Tag ids (EditTask's `selected.forgeTagsIds`).
    // Drives which CMLs the builder offers.
    tagIds: { type: Array, default: () => [] },
    // Scopes to pre-stage for a NEW task (created from a monitoring row). Each:
    // { cmlRecordId, tmlPointId?, thicknessReadingId?, label? }. Ignored for
    // existing tasks (their scopes load from the server). Default [] = today's
    // behavior, so all other call sites are unaffected.
    seedScopes: { type: Array, default: () => [] },
  },

  data() {
    return {
      // Builder option lists (each level loads lazily from the level above).
      cmlOptions: [], // [{ id, label, cmlNo, cmlType }]
      tmlOptions: [], // [{ id, label, position }]
      readingOptions: [], // [{ id, label, value, readingDate }]
      // Current builder selection (the in-progress scope, not yet added).
      builderCmlId: null,
      builderTmlId: null,
      builderReadingId: null,
      loadingCmls: false,
      loadingTmls: false,
      loadingReadings: false,
      loadingExisting: false,
      // Desired end-state set of scopes. Seeded from the server for existing tasks,
      // then add/remove mutate it. commit() diffs this against `existingLinks`.
      // Each: { cmlRecordId, tmlPointId, thicknessReadingId, key, label }
      selectedScopes: [],
      // Server truth at load (existing tasks): the rows we may need to unlink.
      // Each: { id, cmlRecordId, tmlPointId, thicknessReadingId }
      existingLinks: [],
      // Transient one-liner feedback (duplicate add, etc.).
      notice: '',
      noticeTimer: null,
    };
  },

  computed: {
    cleanTagIds() {
      return (this.tagIds || []).filter(Boolean);
    },
    hasTags() {
      return this.cleanTagIds.length > 0;
    },
    // Stable key so the watcher only fires on real tag-set changes.
    tagIdsKey() {
      return this.cleanTagIds.slice().sort().join(',');
    },
    // The diff between the desired set (selectedScopes) and server truth
    // (existingLinks). Single source of truth for both the hint and commit().
    pendingChanges() {
      const existingByKey = new Map(this.existingLinks.map((l) => [this.scopeKey(l), l]));
      const selectedKeys = new Set(this.selectedScopes.map((s) => s.key));
      return {
        toAdd: this.selectedScopes.filter((s) => !existingByKey.has(s.key)),
        toRemove: this.existingLinks.filter((l) => !selectedKeys.has(this.scopeKey(l))),
      };
    },
    // Unsaved scope changes exist → the parent should enable its Save button.
    isDirty() {
      return this.pendingChanges.toAdd.length > 0 || this.pendingChanges.toRemove.length > 0;
    },
    hint() {
      if (!this.hasTags) {
        return 'Add a Tag above first — monitoring locations are listed per tag.';
      }
      const selected = this.selectedScopes.length;
      const { toAdd, toRemove } = this.pendingChanges;

      if (selected === 0) {
        if (toRemove.length > 0) {
          return `All ${toRemove.length} linked scope${toRemove.length === 1 ? '' : 's'} will be removed when you save.`;
        }
        return 'Pick a CML (optionally drill into a TML point or reading), then Add.';
      }
      // New task: every scope is new, so the simple future-tense framing is accurate.
      if (this.isNew) {
        return `${selected} monitoring scope${selected === 1 ? '' : 's'} will be linked when you save.`;
      }
      // Existing task with no pending edits: they are already saved, not "will be".
      if (toAdd.length === 0 && toRemove.length === 0) {
        return `${selected} monitoring scope${selected === 1 ? '' : 's'} linked.`;
      }
      // Existing task with edits: report what's CURRENTLY linked (existingLinks),
      // then what saving will change — using `selected` here would wrongly count
      // not-yet-saved additions as "linked".
      const bits = [];
      if (toAdd.length) bits.push(`${toAdd.length} to add`);
      if (toRemove.length) bits.push(`${toRemove.length} to remove`);
      const linkedNow = this.existingLinks.length;
      return `${linkedNow} linked · ${bits.join(', ')} on save.`;
    },
  },

  watch: {
    // Existing links depend on the task identity.
    taskId: {
      immediate: true,
      handler() {
        this.loadExisting();
      },
    },
    // The CML option set depends on the task's tag selection. A tag change resets
    // the in-progress builder (its CML may no longer be offered).
    tagIdsKey: {
      immediate: true,
      handler() {
        this.resetBuilder();
        this.loadCmls();
      },
    },
    // Cascading resets (the heart of a guided builder): changing CML clears the TML
    // + reading selection and their option lists, then loads the new CML's TMLs.
    builderCmlId() {
      this.builderTmlId = null;
      this.builderReadingId = null;
      this.readingOptions = [];
      this.loadTmls();
    },
    // Changing TML clears the reading selection, then loads the new TML's readings.
    builderTmlId() {
      this.builderReadingId = null;
      this.loadReadings();
    },
    // Tell the parent whether there are unsaved scope changes, so its Save button
    // enables even when no task field changed (the parent tracks fields, not scopes).
    isDirty: {
      immediate: true,
      handler(val) {
        this.$emit('scopes-dirty', val);
      },
    },
  },

  beforeDestroy() {
    if (this.noticeTimer) clearTimeout(this.noticeTimer);
  },

  methods: {
    // Composite identity for a scope. A null narrowing level → empty segment, so
    // "CML-only" and "CML→TML" produce different keys and never collide.
    scopeKey(s) {
      return `${s.cmlRecordId}|${s.tmlPointId || ''}|${s.thicknessReadingId || ''}`;
    },

    // Map the seedScopes prop into staged scope objects (key + label), so a new
    // task created from a monitoring row starts with its originating scope
    // pre-linked. Uses the caller-supplied label, falling back to a breadcrumb.
    buildSeedScopes() {
      return (this.seedScopes || [])
        .filter((s) => s && s.cmlRecordId)
        .map((s) => {
          const scope = {
            cmlRecordId: s.cmlRecordId,
            tmlPointId: s.tmlPointId || null,
            thicknessReadingId: s.thicknessReadingId || null,
          };
          scope.key = this.scopeKey(scope);
          scope.label =
            s.label ||
            this.breadcrumb({
              cmlRecordId: scope.cmlRecordId,
              tmlPointId: scope.tmlPointId,
              thicknessReadingId: scope.thicknessReadingId,
            });
          return scope;
        });
    },

    shortDate(d) {
      if (!d) return '';
      try {
        return new Date(d).toLocaleDateString();
      } catch (err) {
        return '';
      }
    },

    // Thickness with FP noise trimmed (a SQL float can serialize as 9.3499999…).
    formatThickness(value) {
      if (value == null) return '';
      return `${Math.round(value * 1000) / 1000}mm`;
    },

    // Build a human breadcrumb from whatever depth is set, with GUID-slice
    // fallbacks so a label never renders a raw id.
    breadcrumb({ cmlNo, position, value, readingDate, cmlRecordId, tmlPointId, thicknessReadingId }) {
      const parts = [cmlNo || `CML ${(cmlRecordId || '').slice(0, 6)}`];
      if (tmlPointId) {
        parts.push(position || `TML ${tmlPointId.slice(0, 6)}`);
      }
      if (thicknessReadingId) {
        const v = this.formatThickness(value);
        const d = readingDate ? ` · ${this.shortDate(readingDate)}` : '';
        parts.push(`${v}${d}` || `Reading ${thicknessReadingId.slice(0, 6)}`);
      }
      return parts.join(' › ');
    },

    resetBuilder() {
      this.builderCmlId = null;
      this.builderTmlId = null;
      this.builderReadingId = null;
      this.tmlOptions = [];
      this.readingOptions = [];
    },

    flashNotice(message) {
      this.notice = message;
      if (this.noticeTimer) clearTimeout(this.noticeTimer);
      this.noticeTimer = setTimeout(() => {
        this.notice = '';
        this.noticeTimer = null;
      }, 3000);
    },

    clearNotice() {
      this.notice = '';
      if (this.noticeTimer) {
        clearTimeout(this.noticeTimer);
        this.noticeTimer = null;
      }
    },

    async loadCmls() {
      if (!this.hasTags) {
        this.cmlOptions = [];
        return;
      }
      this.loadingCmls = true;
      try {
        const data = await listCmlOptions(this.cleanTagIds);
        this.cmlOptions = (data || []).map((c) => ({
          id: c.id,
          cmlNo: c.cmlNo,
          cmlType: c.cmlType,
          label: c.cmlType ? `${c.cmlNo} · ${c.cmlType}` : c.cmlNo,
        }));
      } catch (err) {
        debugError('[TaskCmlLinksSection] loadCmls failed', err);
        this.cmlOptions = [];
      } finally {
        this.loadingCmls = false;
      }
    },

    async loadTmls() {
      if (!this.builderCmlId) {
        this.tmlOptions = [];
        return;
      }
      this.loadingTmls = true;
      try {
        const data = await listTmlOptions(this.builderCmlId);
        this.tmlOptions = (data || []).map((t) => ({
          id: t.id,
          position: t.position,
          label: t.position || `TML ${t.id.slice(0, 6)}`,
        }));
      } catch (err) {
        debugError('[TaskCmlLinksSection] loadTmls failed', err);
        this.tmlOptions = [];
      } finally {
        this.loadingTmls = false;
      }
    },

    async loadReadings() {
      if (!this.builderTmlId) {
        this.readingOptions = [];
        return;
      }
      this.loadingReadings = true;
      try {
        const data = await listReadingOptions(this.builderTmlId);
        this.readingOptions = (data || []).map((r) => ({
          id: r.id,
          value: r.value,
          readingDate: r.readingDate,
          label: `${this.formatThickness(r.value)} · ${this.shortDate(r.readingDate)}`,
        }));
      } catch (err) {
        debugError('[TaskCmlLinksSection] loadReadings failed', err);
        this.readingOptions = [];
      } finally {
        this.loadingReadings = false;
      }
    },

    async loadExisting() {
      // New tasks have no server links yet; seed from seedScopes (empty by
      // default → unchanged for every existing call site).
      if (this.isNew || !this.taskId) {
        this.existingLinks = [];
        this.selectedScopes = this.buildSeedScopes();
        return;
      }
      this.loadingExisting = true;
      try {
        const links = await listTaskCmlLinks(this.taskId);
        this.existingLinks = (links || []).map((l) => ({
          id: l.id,
          cmlRecordId: l.cmlRecordId,
          tmlPointId: l.tmlPointId || null,
          thicknessReadingId: l.thicknessReadingId || null,
        }));
        this.selectedScopes = (links || []).map((l) => {
          const scope = {
            cmlRecordId: l.cmlRecordId,
            tmlPointId: l.tmlPointId || null,
            thicknessReadingId: l.thicknessReadingId || null,
          };
          scope.key = this.scopeKey(scope);
          scope.label = this.breadcrumb({
            cmlNo: l.cmlNo,
            position: l.tmlPosition,
            value: l.readingValue,
            readingDate: l.readingDate,
            cmlRecordId: l.cmlRecordId,
            tmlPointId: l.tmlPointId,
            thicknessReadingId: l.thicknessReadingId,
          });
          return scope;
        });
      } catch (err) {
        debugError('[TaskCmlLinksSection] loadExisting failed', err);
        this.existingLinks = [];
        this.selectedScopes = [];
      } finally {
        this.loadingExisting = false;
      }
    },

    // Stage the in-progress builder selection as a scope, then clear the builder
    // for the next one. Surfaces a notice (instead of silently no-op'ing) when the
    // scope is already in the list.
    addScope() {
      if (!this.builderCmlId) return;
      const cml = this.cmlOptions.find((o) => o.id === this.builderCmlId);
      const tml = this.builderTmlId ? this.tmlOptions.find((o) => o.id === this.builderTmlId) : null;
      const reading = this.builderReadingId
        ? this.readingOptions.find((o) => o.id === this.builderReadingId)
        : null;

      const scope = {
        cmlRecordId: this.builderCmlId,
        tmlPointId: this.builderTmlId || null,
        thicknessReadingId: this.builderReadingId || null,
      };
      scope.key = this.scopeKey(scope);
      scope.label = this.breadcrumb({
        cmlNo: cml ? cml.cmlNo || cml.label : null,
        position: tml ? tml.position : null,
        value: reading ? reading.value : null,
        readingDate: reading ? reading.readingDate : null,
        cmlRecordId: scope.cmlRecordId,
        tmlPointId: scope.tmlPointId,
        thicknessReadingId: scope.thicknessReadingId,
      });

      if (this.selectedScopes.some((s) => s.key === scope.key)) {
        this.flashNotice(`Already added: ${scope.label}`);
        this.resetBuilder();
        return;
      }
      this.selectedScopes.push(scope);
      this.clearNotice();
      this.resetBuilder();
    },

    removeScope(key) {
      this.selectedScopes = this.selectedScopes.filter((s) => s.key !== key);
    },

    /**
     * Persist the staged scopes against the (now-saved) task: create links for
     * newly-staged scopes, remove links the user deleted. Diffs by composite key
     * (via pendingChanges) so each depth is reconciled independently. Called by the
     * parent form AFTER the task save resolves. Never throws.
     */
    async commit(taskId) {
      if (!taskId) return;
      const { toAdd, toRemove } = this.pendingChanges;

      try {
        for (const s of toAdd) {
          await linkTaskScope(s.cmlRecordId, {
            taskId,
            tmlPointId: s.tmlPointId,
            thicknessReadingId: s.thicknessReadingId,
          });
        }
        for (const link of toRemove) {
          await unlinkCmlTask(link.cmlRecordId, link.id);
        }
        debugLog('[TaskCmlLinksSection] committed', {
          taskId,
          added: toAdd.length,
          removed: toRemove.length,
        });
      } catch (err) {
        debugError('[TaskCmlLinksSection] commit failed (the task itself saved OK)', err);
        if (this.$dialog && this.$dialog.notify && this.$dialog.notify.error) {
          this.$dialog.notify.error(
            'Task saved, but linking monitoring locations failed. Re-open the task to retry.',
            { position: 'top-right', timeout: 4000 },
          );
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.task-scope-builder__actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.task-scope-builder__loading {
  display: flex;
  align-items: center;
  margin-top: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}

.task-scope-builder__list {
  margin-top: 4px;
}

.task-scope-builder__item {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  margin-bottom: 4px;
  font-size: 13px;
}

.task-scope-builder__item-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-scope-builder__notice {
  margin-top: 6px;
  font-size: 12px;
  color: #8a6d00;
}

.task-scope-builder__hint {
  margin-top: 6px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}
</style>
